/**
 * Full-market scenes (batch 3), second generation: technical safety diagrams
 * built from the shared blueprint vocabulary (scenes-shared.tsx / css.ts).
 * Persistent scenery sits outside `<On>`; every guided step owns a complete
 * static frame with its own subject, dimensions and verdict.
 */
import { Blueprint, blueprintIds, Dim, On, Panel, Solid, Verdict } from './scenes-shared';

/** Verdict square for tables - ✓ / ✗ / - as a filled 14-unit cell mark. */
function Sq({ kind, x, y }: { kind: 'ok' | 'no' | 'lim'; x: number; y: number }) {
  const cls = kind === 'ok' ? 'hbB-ok-fill' : kind === 'no' ? 'hbB-no-fill' : 'hbB-warn-fill';
  return <rect className={cls} x={x} y={y} width="14" height="14" rx="2" />;
}

/** Site entry as a three-station flow: PPE on → sign the register → briefed under the OSH plan board. */
export function SiteInductionScene({ en }: { en: boolean }) {
  const ids = blueprintIds('siteInduction');
  const stations = [
    { x: 16, title: en ? 'PPE' : 'OOPP' },
    { x: 170, title: en ? 'Register' : 'Evidence' },
    { x: 324, title: en ? 'OSH plan' : 'Plán BOZP' },
  ];
  return (
    <Blueprint
      id="siteInduction"
      label={en ? 'Site entry in three steps' : 'Vstup na staveniště ve třech krocích'}
      title={en ? 'Site entry · 3 steps' : 'Vstup na staveniště · 3 kroky'}
      source="z. 309/2006 Sb. §14-15 · NV 591/2006 Sb."
    >
      {stations.map((s, i) => (
        <Panel key={s.title} x={s.x} y={38} width={140} height={200} n={i + 1}>
          <text className="hbB-soft" x="40" y="32">
            {s.title}
          </text>
        </Panel>
      ))}
      <line
        className="hbB-dim"
        x1="150"
        y1="136"
        x2="176"
        y2="136"
        markerEnd={`url(#${ids.arrow})`}
      />
      <line
        className="hbB-dim"
        x1="304"
        y1="136"
        x2="330"
        y2="136"
        markerEnd={`url(#${ids.arrow})`}
      />

      {/* 1 · worker in helmet + hi-vis - the sequence is cumulative: art stays once reached */}
      <On step="1">
        <rect className="hbB-focus" x="12" y="34" width="148" height="208" rx="8" />
        <Verdict kind="ok" x={132} y={208} />
      </On>
      <On step="1 2 3">
        <g transform="translate(16 38)">
          <path className="hbB-draw" d="M46 66 a18 18 0 0 1 36 0" />
          <path className="hbB-draw" d="M42 66 h44" />
          <circle className="hbB-draw" cx="64" cy="80" r="11" />
          <path className="hbB-draw" d="M44 100 h40 v56 h-40 z" />
          <path className="hbB-lim" d="M44 118 h40 M44 138 h40" />
          <path className="hbB-draw" d="M48 156 v28 M80 156 v28 M44 184 h8 M76 184 h8" />
          <path className="hbB-leader" d="M84 60 h20" />
          <text className="hbB-lbl" x="88" y="54">
            {en ? 'helmet' : 'přilba'}
          </text>
          <path className="hbB-leader" d="M84 128 h20" />
          <text className="hbB-lbl" x="88" y="122">
            {en ? 'hi-vis' : 'vesta'}
          </text>
        </g>
      </On>

      {/* 2 · the site register: header row, one signed line, pen */}
      <On step="2">
        <rect className="hbB-focus" x="166" y="34" width="148" height="208" rx="8" />
        <Verdict kind="ok" x={284} y={222} />
      </On>
      <On step="2 3">
        <g transform="translate(180 82)">
          <rect className="hbB-head" x="0" y="0" width="120" height="26" />
          <text className="hbB-soft" x="8" y="19">
            {en ? 'name' : 'jméno'}
          </text>
          <text className="hbB-soft" x="76" y="19">
            {en ? 'time' : 'čas'}
          </text>
          <rect className="hbB-cell" x="0" y="26" width="120" height="30" />
          <path className="hbB-draw" d="M8 46 q8 -10 14 0 t14 0 t12 -4" />
          <text className="hbB-mono" x="76" y="47">
            7:00
          </text>
          <rect className="hbB-cell" x="0" y="56" width="120" height="30" />
          <rect className="hbB-cell" x="0" y="86" width="120" height="30" />
          <g className="hbA-pen">
            <path className="hbB-draw-heavy" d="M74 116 l30 -30" />
            <path className="hbB-ink" d="M74 116 l-6 8 l8 -4 z" />
          </g>
        </g>
      </On>

      {/* 3 · the OSH plan board with the risks listed */}
      <On step="3">
        <rect className="hbB-focus" x="320" y="34" width="148" height="208" rx="8" />
        <g transform="translate(334 84)">
          <rect className="hbB-head" x="0" y="0" width="120" height="26" />
          <text className="hbB-lbl" x="8" y="19">
            {en ? 'OSH PLAN' : 'PLÁN BOZP'}
          </text>
          <rect className="hbB-cell" x="0" y="26" width="120" height="96" />
          <Sq kind="ok" x={8} y={36} />
          <text className="hbB-soft" x="30" y="48">
            {en ? 'risks' : 'rizika'}
          </text>
          <Sq kind="ok" x={8} y={64} />
          <text className="hbB-soft" x="30" y="76">
            {en ? 'coordinator' : 'koordinátor'}
          </text>
          <Sq kind="ok" x={8} y={92} />
          <text className="hbB-soft" x="30" y="104">
            {en ? 'first aid' : 'první pomoc'}
          </text>
          <path className="hbB-draw" d="M20 122 v40 M100 122 v40" />
          <Verdict kind="ok" x={100} y={142} />
        </g>
      </On>
    </Blueprint>
  );
}

/** Pre-use tool check: damaged guard + nicked cord ✗ (step 1) vs. guard on, cord and plug intact ✓ (step 2). */
export function ToolCheckScene({ en }: { en: boolean }) {
  const rows = [en ? 'guard' : 'kryt', en ? 'cord' : 'přívod', en ? 'plug' : 'vidlice'];
  return (
    <Blueprint
      id="toolCheck"
      label={en ? 'Pre-use tool check' : 'Kontrola nářadí před použitím'}
      title={en ? 'Pre-use check · angle grinder' : 'Kontrola před použitím · bruska'}
      source="NV 378/2001 Sb. · ČSN 33 1600 ed. 2"
    >
      {/* grinder body - persistent line drawing */}
      <path className="hbB-draw" d="M42 132 h48 v34 h-48 a8 8 0 0 1 -8 -8 v-18 a8 8 0 0 1 8 -8 z" />
      <path
        className="hbB-draw-heavy"
        d="M90 124 h120 a10 10 0 0 1 10 10 v30 a10 10 0 0 1 -10 10 h-120 z"
      />
      <path className="hbB-draw" d="M150 124 v-36 a6 6 0 0 1 12 0 v36" />
      <path className="hbB-draw" d="M220 140 h20 M220 158 h20" />
      <circle className="hbB-draw" cx="276" cy="150" r="36" />
      <circle className="hbB-ink" cx="276" cy="150" r="6" />
      {/* plug */}
      <rect className="hbB-draw" x="30" y="206" width="30" height="20" rx="4" />
      <path className="hbB-draw" d="M22 211 h8 M22 221 h8" />

      {/* checklist table */}
      <g transform="translate(336 44)">
        <rect className="hbB-head" x="0" y="0" width="128" height="30" />
        <text className="hbB-soft" x="8" y="21">
          {en ? 'Check' : 'Kontrola'}
        </text>
        {rows.map((r, i) => (
          <g key={r} transform={`translate(0 ${30 + i * 34})`}>
            <rect className="hbB-cell" x="0" y="0" width="128" height="34" />
            <text className="hbB-lbl" x="8" y="23">
              {r}
            </text>
          </g>
        ))}
      </g>

      {/* 1 · cracked guard, gashed cord - out of service */}
      <On step="1">
        <path className="hbB-draw-heavy" d="M232 128 a46 46 0 0 1 88 24" />
        <path className="hbB-no" d="M262 108 l6 10 l-8 8 l8 10" />
        <path className="hbB-draw-heavy" d="M42 150 q-16 20 -12 42" />
        <path className="hbB-draw-heavy" d="M22 206 q-4 -6 -2 -14" />
        <path className="hbB-lim" d="M30 192 l-8 -8 M32 198 l-9 -7 M27 188 l-6 -9" />
        <circle className="hbB-focus" cx="26" cy="193" r="16" />
        <path className="hbB-leader" d="M270 100 v-30 h20" />
        <text className="hbB-no-txt" x="294" y="76">
          {en ? 'cracked guard' : 'prasklý kryt'}
        </text>
        <path className="hbB-leader" d="M42 193 h24" />
        <text className="hbB-no-txt" x="70" y="198">
          {en ? 'nicked cord' : 'naříznutý přívod'}
        </text>
        <g transform="translate(120 208)">
          <rect className="hbB-no-soft" x="0" y="0" width="150" height="30" rx="4" />
          <text className="hbB-no-txt" x="10" y="21">
            {en ? 'OUT OF SERVICE' : 'MIMO PROVOZ'}
          </text>
        </g>
        <Sq kind="no" x={444} y={84} />
        <Sq kind="no" x={444} y={118} />
        <Sq kind="lim" x={444} y={152} />
        <Verdict kind="no" x={400} y={200} />
      </On>

      {/* 2 · guard on, one continuous cord to an intact plug */}
      <On step="2">
        <path className="hbB-draw-heavy" d="M232 128 a46 46 0 0 1 88 24" />
        <path className="hbB-draw-heavy" d="M42 150 q-20 26 -8 56 q4 8 -6 8 q-8 0 -8 -8" />
        <path className="hbB-leader" d="M270 100 v-30 h20" />
        <text className="hbB-ok-txt" x="294" y="76">
          {en ? 'guard on' : 'kryt nasazen'}
        </text>
        <path className="hbB-leader" d="M62 216 h30" />
        <text className="hbB-ok-txt" x="96" y="221">
          {en ? 'cord + plug intact' : 'přívod + vidlice OK'}
        </text>
        <Sq kind="ok" x={444} y={84} />
        <Sq kind="ok" x={444} y={118} />
        <Sq kind="ok" x={444} y={152} />
        <Verdict kind="ok" x={400} y={200} />
      </On>
    </Blueprint>
  );
}

/** Ladder contact: three points of contact ✓ vs. both hands full + leaning out ✗. */
export function LadderContactScene({ en }: { en: boolean }) {
  const ids = blueprintIds('ladderContact');
  // rails at 4 : 1 - base (285,232) → wall (330,52); rung x = 285 + (232 − y) / 4
  const rungs = [212, 192, 172, 152, 132, 112, 92, 72];
  return (
    <Blueprint
      id="ladderContact"
      label={en ? 'Three points of contact on a ladder' : 'Tři body opory na žebříku'}
      title={en ? 'Ladder · points of contact' : 'Žebřík · body opory'}
      source="NV 362/2005 Sb., příl. čl. III"
    >
      <Solid hatch={ids.hatch} x={40} y={232} width={400} height={12} edge="top" />
      <Solid hatch={ids.hatch} x={330} y={40} width={12} height={192} edge="left" />
      <g className="hbB-draw-heavy">
        <line x1="285" y1="232" x2="330" y2="52" />
        <line x1="271" y1="232" x2="316" y2="52" />
      </g>
      <g className="hbB-draw">
        {rungs.map((y) => {
          const x = 285 + (232 - y) / 4;
          return <line key={y} x1={x - 14} y1={y} x2={x} y2={y} />;
        })}
      </g>
      <path className="hbB-dim" d="M312 232 A28 28 0 0 0 292 204" />
      <text className="hbB-dim-txt" x="238" y="226" textAnchor="end">
        4 : 1
      </text>

      {/* rule table */}
      <g transform="translate(16 40)">
        <rect className="hbB-head" x="0" y="0" width="176" height="28" />
        <text className="hbB-soft" x="8" y="20">
          {en ? 'Contact' : 'Body opory'}
        </text>
        <rect className="hbB-cell" x="0" y="28" width="176" height="32" />
        <text className="hbB-soft" x="8" y="49">
          {en ? '2 feet · 1 hand' : '2 nohy · 1 ruka'}
        </text>
        <Sq kind="ok" x={154} y={37} />
        <rect className="hbB-cell" x="0" y="60" width="176" height="32" />
        <text className="hbB-soft" x="8" y="81">
          {en ? '2 hands · 1 foot' : '2 ruce · 1 noha'}
        </text>
        <Sq kind="ok" x={154} y={69} />
        <rect className="hbB-cell" x="0" y="92" width="176" height="32" />
        <text className="hbB-soft" x="8" y="113">
          {en ? 'both hands full' : 'obě ruce plné'}
        </text>
        <Sq kind="no" x={154} y={101} />
      </g>

      {/* 1 · two feet + one hand on the ladder, free hand carries the bag */}
      <On step="1">
        <rect className="hbB-focus" x="12" y="64" width="184" height="68" rx="4" />
        <circle className="hbB-draw" cx="262" cy="84" r="11" />
        <path className="hbB-draw" d="M266 95 l10 55" />
        <path className="hbB-draw" d="M268 104 l36 8" />
        <path className="hbB-draw" d="M268 104 l-16 34" />
        <rect className="hbB-draw" x="240" y="138" width="22" height="18" rx="3" />
        <path className="hbB-draw" d="M276 150 l16 22 M276 150 l12 42" />
        <g className="hbB-ok">
          <circle cx="304" cy="112" r="7" />
          <circle cx="292" cy="172" r="7" />
          <circle cx="288" cy="192" r="7" />
        </g>
        <text className="hbB-ok-txt" x="318" y="108">
          1
        </text>
        <text className="hbB-ok-txt" x="306" y="176">
          2
        </text>
        <text className="hbB-ok-txt" x="302" y="200">
          3
        </text>
        <text className="hbB-ok-txt" x="20" y="196">
          {en ? '3 points hold' : '3 body opory drží'}
        </text>
        <Verdict kind="ok" x={226} y={60} />
      </On>

      {/* 2 · both hands on a box, body leaning out - only the feet remain */}
      <On step="2">
        <rect className="hbB-focus" x="12" y="128" width="184" height="36" rx="4" />
        <path className="hbB-ghost" d="M236 100 q-30 60 -60 130" />
        <circle className="hbB-draw" cx="236" cy="88" r="11" />
        <path className="hbB-draw" d="M244 99 l28 51" />
        <path className="hbB-draw" d="M248 108 l-22 12 M250 116 l-20 18" />
        <rect className="hbB-draw" x="196" y="108" width="34" height="30" rx="3" />
        <path className="hbB-draw" d="M272 150 l20 22 M272 150 l16 42" />
        <g className="hbB-ok">
          <circle cx="292" cy="172" r="7" />
          <circle cx="288" cy="192" r="7" />
        </g>
        <circle className="hbB-no" cx="304" cy="112" r="7" />
        <path className="hbB-no" d="M300 108 l8 8 M308 108 l-8 8" />
        <path className="hbB-no" d="M258 130 h-38" />
        <path className="hbB-no-fill" d="M220 130 l10 -5 v10 z" />
        <text className="hbB-no-txt" x="20" y="196">
          {en ? 'leaning · 0 hands' : 'vyklánění · 0 rukou'}
        </text>
        <Verdict kind="no" x={210} y={60} />
      </On>
    </Blueprint>
  );
}

/** Chainsaw kickback: tip's upper quarter bites, bar arcs at the head ✗ vs. two-hand grip, lower edge mid-bar ✓. */
export function ChainsawKickbackScene({ en }: { en: boolean }) {
  const ids = blueprintIds('chainsawKickback');
  // saw in local coordinates: bar root at (0,0), bar to the right
  const saw = (
    <g>
      <rect className="hbB-draw" x="-118" y="2" width="30" height="30" rx="8" />
      <path
        className="hbB-draw-heavy"
        d="M-90 -6 h82 a8 8 0 0 1 8 8 v28 a8 8 0 0 1 -8 8 h-82 a8 8 0 0 1 -8 -8 v-28 a8 8 0 0 1 8 -8 z"
      />
      <path className="hbB-draw" d="M-72 -6 v-22 a8 8 0 0 1 8 -8 h44 a8 8 0 0 1 8 8 v22" />
      <path className="hbB-draw" d="M-8 -6 v-14 h-12" />
      <rect className="hbB-draw-heavy" x="0" y="8" width="130" height="14" rx="7" />
      <rect
        className="hbB-thin"
        x="-2"
        y="6"
        width="134"
        height="18"
        rx="9"
        strokeDasharray="4 3"
      />
    </g>
  );
  return (
    <Blueprint
      id="chainsawKickback"
      label={en ? 'Chainsaw kickback' : 'Zpětný vrh motorové pily'}
      title={en ? 'Kickback · bar tip' : 'Zpětný vrh · špička lišty'}
      source="NV 339/2017 Sb."
    >
      <Solid hatch={ids.hatch} x={40} y={236} width={400} height={10} edge="top" />

      {/* 1 · tip cut into a standing stem - the upper quarter kicks the bar back at the head */}
      <On step="1">
        <Solid hatch={ids.hatch} x={300} y={120} width={80} height={116} edge="left" />
        <g transform="translate(172 145)">{saw}</g>
        <path className="hbB-no-fill" d="M295 160 v-12 a12 12 0 0 1 12 12 z" />
        <circle className="hbB-draw" cx="104" cy="72" r="14" />
        <path className="hbB-draw" d="M84 112 a20 18 0 0 1 40 0" />
        <path className="hbB-no" d="M300 150 q-80 -110 -170 -66" strokeDasharray="8 6" />
        <path className="hbB-no-fill" d="M124 84 l14 -6 l-2 12 z" />
        {/* detail: the tip enlarged */}
        <g transform="translate(400 82)">
          <circle className="hbB-card" cx="0" cy="0" r="38" />
          <path className="hbB-draw-heavy" d="M-30 -12 h10 a12 12 0 0 1 0 24 h-10" />
          <path className="hbB-no-fill" d="M-20 0 v-12 a12 12 0 0 1 12 12 z" />
          <path className="hbB-thin" d="M-20 -14 v28 M-22 0 h28" strokeDasharray="3 3" />
        </g>
        <path className="hbB-leader" d="M307 150 l30 -30 h24" />
        <text className="hbB-no-txt" x="356" y="140">
          {en ? 'upper ¼ of tip' : 'horní ¼ špičky'}
        </text>
        <text className="hbB-no-txt" x="130" y="52">
          {en ? 'bar arcs at head' : 'lišta letí k hlavě'}
        </text>
        <Verdict kind="no" x={420} y={200} />
      </On>

      {/* 2 · log on the ground, lower edge of the bar mid-length, both hands on the grips */}
      <On step="2">
        <Solid hatch={ids.hatch} x={150} y={200} width={250} height={36} edge="top" />
        <path className="hbB-paper" d="M196 200 h50 v10 h-50 z" />
        <g transform="translate(140 180)">{saw}</g>
        <g className="hbB-ok">
          <circle cx="37" cy="197" r="9" />
          <circle cx="98" cy="150" r="9" />
        </g>
        <path className="hbB-leader" d="M98 141 v-40 h16" />
        <text className="hbB-ok-txt" x="118" y="106">
          {en ? 'both hands on grips' : 'obě ruce na rukojetích'}
        </text>
        <path className="hbB-leader" d="M128 160 v-20 h16" />
        <text className="hbB-ok-txt" x="148" y="145">
          {en ? 'chain brake' : 'brzda řetězu'}
        </text>
        <path className="hbB-leader" d="M220 202 v22 h16" />
        <text className="hbB-ok-txt" x="240" y="229">
          {en ? 'lower edge, mid-bar' : 'spodní hrana, střed lišty'}
        </text>
        <Verdict kind="ok" x={420} y={130} />
      </On>
    </Blueprint>
  );
}

/** Suspected asbestos: drill meets an old grey board → STOP; no drilling, sweeping or vacuuming; licensed firm + KHS. */
export function AsbestosStopScene({ en }: { en: boolean }) {
  const ids = blueprintIds('asbestosStop');
  return (
    <Blueprint
      id="asbestosStop"
      label={en ? 'Suspected asbestos - stop work' : 'Podezření na azbest - stop práce'}
      title={en ? 'Suspected asbestos' : 'Podezření na azbest'}
      source="z. 258/2000 Sb. §41 · NV 361/2007 Sb. §21"
    >
      {/* old corrugated fibre-cement board - persistent */}
      <rect className="hbB-head" x="300" y="60" width="150" height="176" />
      <rect x="300" y="60" width="150" height="176" fill={`url(#${ids.hatch})`} opacity="0.35" />
      <path className="hbB-draw-heavy" d="M300 60 q15 -12 30 0 t30 0 t30 0 t30 0 t30 0" />
      <path className="hbB-draw" d="M300 60 v176 h150 v-176" />
      <path className="hbB-leader" d="M330 40 h-30" />
      <text className="hbB-soft" x="336" y="45">
        {en ? 'old grey board' : 'stará šedá deska'}
      </text>

      {/* 1 · drill approaches - STOP */}
      <On step="1">
        <g transform="translate(96 130)">
          <rect className="hbB-draw-heavy" x="0" y="-16" width="90" height="32" rx="8" />
          <rect className="hbB-draw" x="28" y="16" width="26" height="48" rx="6" />
          <path className="hbB-draw" d="M54 22 h10 v10" />
          <rect className="hbB-draw" x="90" y="-8" width="22" height="16" rx="3" />
          <path className="hbB-draw-heavy" d="M112 0 h72" />
        </g>
        <circle className="hbB-focus" cx="290" cy="130" r="18" />
        <g transform="translate(214 72)">
          <path className="hbB-no-fill" d="M-13 -30 h26 l17 17 v26 l-17 17 h-26 l-17 -17 v-26 z" />
          <text className="hbB-on-dark" x="0" y="6" textAnchor="middle">
            STOP
          </text>
        </g>
        <text className="hbB-no-txt" x="20" y="222">
          {en ? 'suspicion = stop work' : 'podezření = stop práce'}
        </text>
        <Verdict kind="no" x={250} y={200} />
      </On>

      {/* 2 · any disturbance releases fibres - the three prohibitions */}
      <On step="2">
        <g transform="translate(16 44)">
          <rect className="hbB-head" x="0" y="0" width="180" height="30" />
          <text className="hbB-soft" x="8" y="21">
            {en ? 'Never' : 'Zákaz'}
          </text>
          {[en ? 'drill' : 'vrtat', en ? 'sweep' : 'zametat', en ? 'vacuum' : 'vysávat'].map(
            (r, i) => (
              <g key={r} transform={`translate(0 ${30 + i * 34})`}>
                <rect className="hbB-cell" x="0" y="0" width="180" height="34" />
                <text className="hbB-lbl" x="8" y="23">
                  {r}
                </text>
                <Sq kind="no" x={156} y={10} />
              </g>
            ),
          )}
        </g>
        <g className="hbA-waft hbB-no">
          <path d="M290 96 l-14 6 M292 124 l-16 -4 M288 150 l-12 8 M292 178 l-16 2 M288 206 l-14 -6" />
        </g>
        <path className="hbB-leader" d="M270 150 h-30" />
        <text className="hbB-no-txt" x="236" y="155" textAnchor="end">
          {en ? 'fibres' : 'vlákna'}
        </text>
        <Verdict kind="no" x={240} y={64} />
      </On>

      {/* 3 · licensed firm removes it, reported to the KHS */}
      <On step="3">
        <g transform="translate(90 96)">
          <path className="hbB-draw" d="M-18 -8 a18 18 0 0 1 36 0 v10 h-36 z" />
          <circle className="hbB-draw" cx="0" cy="0" r="11" />
          <rect className="hbB-ink" x="-8" y="2" width="16" height="8" rx="3" />
          <path className="hbB-draw" d="M-20 20 h40 v58 h-40 z M-14 78 v28 M14 78 v28" />
          <path className="hbB-draw" d="M-20 24 l-14 40 M20 24 l14 40" />
        </g>
        <text className="hbB-soft" x="90" y="230" textAnchor="middle">
          {en ? 'licensed firm' : 'oprávněná firma'}
        </text>
        <g transform="translate(150 50)">
          <rect className="hbB-cell" x="0" y="0" width="60" height="30" rx="4" />
          <text className="hbB-lbl" x="30" y="21" textAnchor="middle">
            {en ? 'firm' : 'firma'}
          </text>
          <line
            className="hbB-dim"
            x1="64"
            y1="15"
            x2="88"
            y2="15"
            markerEnd={`url(#${ids.arrow})`}
          />
          <rect className="hbB-cell" x="90" y="0" width="60" height="30" rx="4" />
          <text className="hbB-lbl" x="120" y="21" textAnchor="middle">
            KHS
          </text>
        </g>
        <rect className="hbB-warn-fill" x="180" y="236" width="280" height="10" />
        <g className="hbB-ink">
          {[190, 230, 270, 310, 350, 390, 430].map((x) => (
            <path key={x} d={`M${x} 236 h14 l-8 10 h-14 z`} />
          ))}
        </g>
        <Verdict kind="ok" x={250} y={140} />
      </On>
    </Blueprint>
  );
}

/** Lone-working check-in loop as a timeline: arrival confirmed → agreed check-ins → a missed one escalates. */
export function CheckInLoopScene({ en }: { en: boolean }) {
  const ids = blueprintIds('checkInLoop');
  const nodes = [
    { x: 100, label: en ? 'On site' : 'Na místě' },
    { x: 200, label: en ? 'check' : 'kontrola' },
    { x: 290, label: en ? 'check' : 'kontrola' },
    { x: 400, label: en ? 'Done' : 'Hotovo' },
  ];
  return (
    <Blueprint
      id="checkInLoop"
      label={en ? 'Lone-working check-in loop' : 'Smyčka kontaktu při osamocené práci'}
      title={en ? 'Check-in loop · lone work' : 'Smyčka kontaktu · osamocená práce'}
      source="ZP §102-103 · NV 101/2005 Sb."
    >
      <line
        className="hbB-draw"
        x1="40"
        y1="160"
        x2="440"
        y2="160"
        markerEnd={`url(#${ids.arrow})`}
      />
      {nodes.map((n) => (
        <g key={n.x}>
          <circle className="hbB-card" cx={n.x} cy="160" r="9" />
          <text className="hbB-soft" x={n.x} y="190" textAnchor="middle">
            {n.label}
          </text>
        </g>
      ))}

      {/* arrival stays confirmed through every later step */}
      <On step="1 2 3">
        <circle className="hbB-ok-fill" cx="100" cy="160" r="9" />
      </On>

      {/* 1 · status "On site" confirmed in the app */}
      <On step="1">
        <rect className="hbB-draw" x="78" y="52" width="44" height="80" rx="8" />
        <path className="hbB-thin" d="M84 66 h32 M84 118 h32" />
        <Sq kind="ok" x={93} y={84} />
        <path className="hbB-leader" d="M122 92 h14" />
        <text className="hbB-ok-txt" x="140" y="97">
          {en ? 'confirmed in the app' : 'potvrzeno v aplikaci'}
        </text>
        <Verdict kind="ok" x={100} y={228} />
      </On>

      {/* 2 · longer job: an agreed check time, repeated */}
      <On step="2">
        <circle className="hbB-ok-fill" cx="200" cy="160" r="9" />
        <circle className="hbB-ok-fill" cx="290" cy="160" r="9" />
        <Dim arrow={ids.arrow} x1={200} y1={100} x2={290} y2={100} />
        <path className="hbB-dim" d="M200 92 v16 M290 92 v16" strokeDasharray="3 3" />
        <text className="hbB-dim-txt" x="245" y="88" textAnchor="middle">
          {en ? 'Δt · agreed' : 'Δt · domluvený'}
        </text>
        <circle className="hbB-draw" cx="240" cy="130" r="14" />
        <path className="hbA-clock-hand hbB-draw" d="M240 130 v-10" />
        <text className="hbB-ok-txt" x="245" y="215" textAnchor="middle">
          {en ? 'confirmed' : 'potvrzeno'}
        </text>
        <Verdict kind="ok" x={245} y={240} />
      </On>

      {/* 3 · a missed check-in escalates to dispatch */}
      <On step="3">
        <circle className="hbB-ok-fill" cx="200" cy="160" r="9" />
        <circle className="hbB-no-fill" cx="290" cy="160" r="9" />
        <path className="hbB-no" d="M290 150 v-48" />
        <path className="hbB-no-fill" d="M290 96 l-6 10 h12 z" />
        <text className="hbB-no-txt" x="300" y="130">
          {en ? 'no reply' : 'bez odezvy'}
        </text>
        <rect className="hbB-no-soft" x="216" y="56" width="190" height="40" rx="6" />
        <text className="hbB-no-txt" x="311" y="82" textAnchor="middle">
          {en ? 'dispatch · 155 / 158' : 'dispečink · 155 / 158'}
        </text>
        <g className="hbA-alert">
          <circle className="hbB-no-fill" cx="216" cy="56" r="11" />
          <text className="hbB-on-dark" x="216" y="62" textAnchor="middle">
            !
          </text>
        </g>
        <Verdict kind="no" x={290} y={228} />
      </On>
    </Blueprint>
  );
}
